import { makeAutoObservable, runInAction } from 'mobx';
import { fieldguideApi } from '../api/fieldguide_api';
import type { FieldguideSection, FieldguideEntry } from '../types';

export class FieldguideStore {
  // Sections
  sections: FieldguideSection[] = [];
  sectionsLoading = false;
  sectionsError: string | null = null;

  // Entries by section slug
  entriesBySectionSlug: Map<string, FieldguideEntry[]> = new Map();
  entriesLoadingBySlug: Map<string, boolean> = new Map();
  entriesLoading = false;
  entriesError: string | null = null;

  // Current entry
  currentEntry: FieldguideEntry | null = null;
  currentEntryLoading = false;
  currentEntryError: string | null = null;

  // Form states
  saving = false;
  saveError: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // ===========================================================================
  // SECTIONS
  // ===========================================================================

  async fetchSections(guideType: string = 'general'): Promise<void> {
    if (this.sectionsLoading) return;

    runInAction(() => {
      this.sectionsLoading = true;
      this.sectionsError = null;
    });

    try {
      const response = await fieldguideApi.listSections(guideType);
      runInAction(() => {
        this.sections = response.items;
        this.sectionsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.sectionsLoading = false;
        this.sectionsError =
          err instanceof Error ? err.message : 'Failed to load sections';
      });
    }
  }

  getSectionBySlug(slug: string): FieldguideSection | undefined {
    return this.sections.find((s) => s.slug === slug);
  }

  async createSection(data: {
    name: string;
    guide_type?: string;
    order?: number;
  }): Promise<FieldguideSection | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const section = await fieldguideApi.createSection(data);
      runInAction(() => {
        this.sections.push(section);
        this.sections.sort((a, b) => a.order - b.order);
        this.saving = false;
      });
      return section;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to create section';
      });
      return null;
    }
  }

  async updateSection(
    slug: string,
    data: { name?: string; guide_type?: string; order?: number }
  ): Promise<FieldguideSection | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const section = await fieldguideApi.updateSection(slug, data);
      runInAction(() => {
        const index = this.sections.findIndex((s) => s.slug === slug);
        if (index !== -1) {
          this.sections[index] = section;
        }
        this.sections.sort((a, b) => a.order - b.order);
        this.saving = false;
      });
      return section;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to update section';
      });
      return null;
    }
  }

  async deleteSection(slug: string): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      await fieldguideApi.deleteSection(slug);
      runInAction(() => {
        this.sections = this.sections.filter((s) => s.slug !== slug);
        this.saving = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to delete section';
      });
      return false;
    }
  }

  // ===========================================================================
  // ENTRIES
  // ===========================================================================

  async fetchEntries(sectionSlug: string): Promise<void> {
    if (this.entriesLoadingBySlug.get(sectionSlug)) return;

    runInAction(() => {
      this.entriesLoadingBySlug.set(sectionSlug, true);
      this.entriesLoading = true;
      this.entriesError = null;
    });

    try {
      const response = await fieldguideApi.listEntries(sectionSlug);
      runInAction(() => {
        this.entriesBySectionSlug.set(sectionSlug, response.items);
        this.entriesLoadingBySlug.set(sectionSlug, false);
        // entriesLoading is true if any section is still loading
        this.entriesLoading = Array.from(this.entriesLoadingBySlug.values()).some(Boolean);
      });
    } catch (err) {
      runInAction(() => {
        this.entriesLoadingBySlug.set(sectionSlug, false);
        this.entriesLoading = Array.from(this.entriesLoadingBySlug.values()).some(Boolean);
        this.entriesError =
          err instanceof Error ? err.message : 'Failed to load entries';
      });
    }
  }

  getEntriesForSection(sectionSlug: string): FieldguideEntry[] {
    return this.entriesBySectionSlug.get(sectionSlug) || [];
  }

  async fetchEntry(entrySlug: string): Promise<void> {
    runInAction(() => {
      this.currentEntryLoading = true;
      this.currentEntryError = null;
    });

    try {
      const entry = await fieldguideApi.getEntry(entrySlug);
      runInAction(() => {
        this.currentEntry = entry;
        this.currentEntryLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.currentEntryLoading = false;
        this.currentEntryError =
          err instanceof Error ? err.message : 'Failed to load entry';
      });
    }
  }

  async createEntry(data: {
    section_slug: string;
    title: string;
    content: string;
    content_type?: string;
    order?: number;
  }): Promise<FieldguideEntry | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const entry = await fieldguideApi.createEntry(data);
      runInAction(() => {
        const existing =
          this.entriesBySectionSlug.get(data.section_slug) || [];
        this.entriesBySectionSlug.set(data.section_slug, [
          ...existing,
          entry,
        ]);
        this.saving = false;
      });
      return entry;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to create entry';
      });
      return null;
    }
  }

  async updateEntry(
    slug: string,
    sectionSlug: string,
    data: {
      title?: string;
      content?: string;
      content_type?: string;
      order?: number;
    }
  ): Promise<FieldguideEntry | null> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      const entry = await fieldguideApi.updateEntry(slug, data);
      runInAction(() => {
        // Update in entries list
        const entries = this.entriesBySectionSlug.get(sectionSlug) || [];
        const index = entries.findIndex((e) => e.slug === slug);
        if (index !== -1) {
          entries[index] = entry;
        }
        // Update current entry if it's the same
        if (this.currentEntry?.slug === slug) {
          this.currentEntry = entry;
        }
        this.saving = false;
      });
      return entry;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to update entry';
      });
      return null;
    }
  }

  async deleteEntry(slug: string, sectionSlug: string): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      await fieldguideApi.deleteEntry(slug);
      runInAction(() => {
        const entries = this.entriesBySectionSlug.get(sectionSlug) || [];
        this.entriesBySectionSlug.set(
          sectionSlug,
          entries.filter((e) => e.slug !== slug)
        );
        if (this.currentEntry?.slug === slug) {
          this.currentEntry = null;
        }
        this.saving = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to delete entry';
      });
      return false;
    }
  }

  // ===========================================================================
  // REORDER
  // ===========================================================================

  async reorder(data: {
    sections?: string[];
    entries?: Record<string, string[]>;
  }): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.saveError = null;
    });

    try {
      await fieldguideApi.reorder(data);
      runInAction(() => {
        // Update local order for sections
        if (data.sections) {
          data.sections.forEach((slug, i) => {
            const section = this.sections.find((s) => s.slug === slug);
            if (section) section.order = i;
          });
          this.sections.sort((a, b) => a.order - b.order);
        }
        // Update local order for entries
        if (data.entries) {
          for (const [sectionSlug, entrySlugs] of Object.entries(
            data.entries
          )) {
            const entries = this.entriesBySectionSlug.get(sectionSlug);
            if (entries) {
              entrySlugs.forEach((slug, i) => {
                const entry = entries.find((e) => e.slug === slug);
                if (entry) entry.order = i;
              });
              entries.sort((a, b) => a.order - b.order);
            }
          }
        }
        this.saving = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.saveError =
          err instanceof Error ? err.message : 'Failed to reorder';
      });
      return false;
    }
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  clearCurrentEntry(): void {
    this.currentEntry = null;
    this.currentEntryError = null;
  }

  clearErrors(): void {
    this.sectionsError = null;
    this.entriesError = null;
    this.currentEntryError = null;
    this.saveError = null;
  }
}

export const fieldguideStore = new FieldguideStore();
